/**
 * useEattextDialog — Track 3 Phase 5
 *
 * Owns the EATTEXT (Enhanced Attribute Extraction) dialog feature
 * (Phase FIN.5) extracted from `App.tsx` as part of the Track 3
 * App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns the `eattextDialogOpen` flag and the `eattextResult`
 *     extraction snapshot. The dialog is otherwise stateless — the
 *     parent recomputes `extractAttributes()` each time the user
 *     changes filters and re-feeds the result.
 *   - Exposes imperative `open()` / `close()` for the command-line
 *     router (`EATTEXT` / `ИЗВЛАТР` / `ДАННЫЕ`) and `resetShellState()`.
 *     `open()` runs an unfiltered `extractAttributes()` over the live
 *     drawing document and seeds `eattextResult` with the snapshot.
 *   - Owns `onFiltersChange` (re-runs the extractor with updated
 *     filters), `onDownload` (CSV → browser download via temporary
 *     anchor), and `onClose` (resets state + closes).
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: {
 *       result, availableBlocks, availableTags,
 *       onFiltersChange, onDownload, onClose,
 *     } | null,  // `null` until `open()` is called
 *     open, close,
 *   }
 *
 * The orchestrator mounts `<AttributeExtractionDialog>` when `isOpen`
 * AND `props !== null`, then spreads `props`. The component preserves
 * the AutoCAD-parity surface (block / tag / visible / constant
 * filters, sortable table, CSV export); the hook only orchestrates
 * the open flag, extraction round-trips, and download wiring.
 *
 * AutoCAD parity:
 *   AutoCAD's EATTEXT command opens a wizard culminating in a
 *   tabular preview and CSV / XLS export. The dialog component
 *   collapses the wizard to a single in-app dialog with live filter
 *   feedback; behaviour mirrors AutoCAD's "Refresh Data" + "Save As"
 *   buttons.
 */

import { useCallback, useMemo, useState } from "react";

import { useDialogOpenState } from "./useDialogOpenState";

import {
  extractAttributes,
  type AttributeExtractionResult,
} from "@dxf-ai-editor/command-engine";

import { useEditorStore } from "../state/editorStore";
import { useT } from "../i18n";

export interface UseEattextDialogParams {
  /** Receives the localized "downloaded N rows" feed entry. */
  appendCommandFeed: (text: string) => void;
}

export interface UseEattextDialogResult {
  /** Whether to mount `<AttributeExtractionDialog>`. */
  isOpen: boolean;
  /**
   * Props bundle for `<AttributeExtractionDialog>`. `null` until
   * `open()` has been called and the initial extraction snapshot has
   * been seeded — the orchestrator should treat `null` as "do not
   * mount" the same as `isOpen=false`.
   */
  props: {
    result: AttributeExtractionResult;
    availableBlocks: string[];
    availableTags: string[];
    onFiltersChange: (filters: {
      blockFilter?: string[];
      attributesFilter?: string[];
      includeInvisible?: boolean;
      includeConstant?: boolean;
    }) => void;
    onDownload: (csv: string) => void;
    onClose: () => void;
  } | null;
  /** Imperative open — used by the command-line router. */
  open: () => void;
  /** Imperative close — used by `resetShellState()` and `onClose`. */
  close: () => void;
}

export function useEattextDialog({
  appendCommandFeed,
}: UseEattextDialogParams): UseEattextDialogResult {
  const t = useT();
  const geometryDocument = useEditorStore((store) => store.geometryDocument);

  const [result, setResult] = useState<AttributeExtractionResult | null>(null);

  const {
    isOpen,
    open: openDialog,
    close,
  } = useDialogOpenState({
    onClose: () => setResult(null),
  });

  const open = useCallback(() => {
    const initial = extractAttributes({ drawingDocument: geometryDocument });
    setResult(initial);
    openDialog();
  }, [geometryDocument, openDialog]);

  // Available block names (excluding anonymous "*..." blocks AutoCAD hides).
  const availableBlocks = useMemo(
    () =>
      Object.keys(geometryDocument.blocks ?? {}).filter(
        (name) => !name.startsWith("*"),
      ),
    [geometryDocument.blocks],
  );

  // Distinct attdef tags across all block definitions.
  const availableTags = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(geometryDocument.blocks ?? {}).flatMap((bd) =>
            bd.entities
              .filter((e) => e.type === "Attdef")
              .map((e) => (e as { tag: string }).tag)
              .filter(Boolean),
          ),
        ),
      ),
    [geometryDocument.blocks],
  );

  const onFiltersChange = useCallback(
    (filters: {
      blockFilter?: string[];
      attributesFilter?: string[];
      includeInvisible?: boolean;
      includeConstant?: boolean;
    }) => {
      const next = extractAttributes({
        drawingDocument: geometryDocument,
        blockFilter: filters.blockFilter,
        attributesFilter: filters.attributesFilter,
        includeInvisible: filters.includeInvisible,
        includeConstant: filters.includeConstant,
      });
      setResult(next);
    },
    [geometryDocument],
  );

  const onDownload = useCallback(
    (csv: string) => {
      // Trigger a browser download via temporary anchor element —
      // avoids leaving the URL object dangling.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "attributes.csv";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      appendCommandFeed(
        t("summary.eattext_downloaded", {
          count: result?.rows.length ?? 0,
        }),
      );
    },
    [appendCommandFeed, t, result],
  );

  return {
    isOpen,
    props: result
      ? {
          result,
          availableBlocks,
          availableTags,
          onFiltersChange,
          onDownload,
          onClose: close,
        }
      : null,
    open,
    close,
  };
}
